import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
    IdsButton,
    IdsDisplayTag,
    IdsLink,
    IdsSectionNotice
} from '@investec/ids-web-react';
import { useFeatureFlags } from '../FeatureFlags/withFeatureFlags';
import { customerDataContext } from '../../contexts/CustomerDataProvider';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';
import IsaTransferInstructionsModal from '../MyAccount/AccountClosure/DISA/IsaTransferInstructionsModal';
import IsaTransferToInvestecStep from './IsaTransferToInvestecStep';
import IsaTransferProviderIntroStep from './IsaTransferProviderIntroStep';
import IsaTransferProviderDetailsStep from './IsaTransferProviderDetailsStep';
import { IsaTransferProviderDetailsData as ProviderDetailsData } from './IsaTransferProviderDetailsStep';
import IsaTransferProviderAmountStep from './IsaTransferProviderAmountStep';
import { ProviderAmountData } from './IsaTransferProviderAmountStep';
import IsaTransferProviderSummaryStep from './IsaTransferProviderSummaryStep';
import IsaTransferSuccessStep from './IsaTransferSuccessStep';
import { isEligibleIsaTransferDestination } from './isaTransferEligibility';

import './isaTransferOptions.scss';

type TransferDirection = 'investec' | 'provider';
type IsaTransferStep = 'options' | 'to-investec' | 'to-provider' | 'to-provider-details' | 'to-provider-amount' | 'to-provider-summary' | 'success';

const hasTransferInProgress = (status?: string): boolean => {
    const normalizedStatus = status?.replace(/[\s_-]/g, '').toLowerCase();
    return normalizedStatus === 'requested' || normalizedStatus === 'inprogress';
};

interface IsaTransferOptionsStepProps {
    accountId: string;
    initialTransferInStatus?: string;
    initialTransferInStatusKnown?: boolean;
    featureEnabled?: (featureName: string) => boolean;
    onBack: () => void;
    onClose: () => void;
}

const IsaTransferOptionsStep: React.FC<IsaTransferOptionsStepProps> = ({
    accountId,
    initialTransferInStatus,
    initialTransferInStatusKnown = false,
    featureEnabled,
    onBack,
    onClose
}) => {
    const navigate = useNavigate();
    const { data: customerData, loading } = useContext(customerDataContext) ?? {};
    const accounts = customerData?.customer?.accounts ?? [];
    const analytics = useUnifiedAnalytics();
    const [flagEnabled] = useFeatureFlags();
    const isFeatureEnabled = featureEnabled ?? flagEnabled;
    const isIsaTransfersDisabled = isFeatureEnabled('DisableISATransfers') === true;
    const sourceAccount = accounts.find(account =>
        String(account.id).toLowerCase() === accountId.toLowerCase()
    );
    const isCustomerDataLoading = loading || !customerData;
    const isTransferStatusLoading = isCustomerDataLoading && !initialTransferInStatusKnown;
    const hasActiveTransferIn = isCustomerDataLoading && initialTransferInStatusKnown
        ? hasTransferInProgress(initialTransferInStatus)
        : hasTransferInProgress(sourceAccount?.transferIn?.status);
    const hasActiveTransferOut = hasTransferInProgress(sourceAccount?.transferOut?.status);
    const hasActiveExternalTransfer = hasActiveTransferIn || hasActiveTransferOut;
    const isProviderTransferDisabled = isIsaTransfersDisabled || isTransferStatusLoading || hasActiveExternalTransfer;
    const hasOtherIsa = accounts.some(account =>
        account.id !== accountId && isEligibleIsaTransferDestination(account)
    );
    const [selectedDirection, setSelectedDirection] = useState<TransferDirection | null>(null);
    const [showTransferOutGuidance, setShowTransferOutGuidance] = useState(false);
    const [currentStep, setCurrentStep] = useState<IsaTransferStep>('options');

    useEffect(() => {
        if (analytics) {
            analytics.trackAnalyticsPageEvent(
                'options view',
                {},
                'ISA Transfer Options',
                'ISA_Transfer_Options|Page_Load|Options_View'
            );
        }
    }, [analytics]);
    const [providerDetails, setProviderDetails] = useState<ProviderDetailsData | null>(null);
    const [amountDetails, setAmountDetails] = useState<ProviderAmountData | null>(null);
    const showEmptyState = selectedDirection === 'investec' && customerData && !loading && !hasOtherIsa;
    const showContinueAction = (hasOtherIsa && selectedDirection === 'investec') || selectedDirection === 'provider';

    useEffect(() => {
        if (isProviderTransferDisabled && selectedDirection === 'provider') {
            setSelectedDirection(null);
        }
    }, [isProviderTransferDisabled, selectedDirection]);

    if (currentStep === 'to-investec') {
        return (
            <IsaTransferToInvestecStep
                accountId={accountId}
                onBack={() => setCurrentStep('options')}
                onOtpVerified={() => setCurrentStep('success')}
            />
        );
    }

    if (currentStep === 'to-provider') {
        return (
            <IsaTransferProviderIntroStep
                onBack={() => setCurrentStep('options')}
                onContinue={() => setCurrentStep('to-provider-details')}
            />
        );
    }

    if (currentStep === 'to-provider-details') {
        return (
            <IsaTransferProviderDetailsStep
                initialData={providerDetails}
                onBack={() => setCurrentStep('to-provider')}
                onContinue={(details) => {
                    setProviderDetails(details);
                    setCurrentStep('to-provider-amount');
                }}
            />
        );
    }

    if (currentStep === 'to-provider-amount') {
        return (
            <IsaTransferProviderAmountStep
                sourceBalance={sourceAccount?.balance ?? 0}
                initialData={amountDetails}
                onBack={() => setCurrentStep('to-provider-details')}
                onContinue={(amount) => {
                    setAmountDetails(amount);
                    setCurrentStep('to-provider-summary');
                }}
            />
        );
    }

    if (currentStep === 'to-provider-summary') {
        return (
            <IsaTransferProviderSummaryStep
                accountId={accountId}
                accountNumber={sourceAccount?.accountNumber ?? ''}
                sortCode={sourceAccount?.sortCode ?? ''}
                productName={sourceAccount?.product?.name ?? ''}
                providerDetails={providerDetails!}
                amountDetails={amountDetails!}
                onBack={() => setCurrentStep('to-provider-amount')}
                onEdit={() => setCurrentStep('to-provider-details')}
                onComplete={() => setCurrentStep('success')}
            />
        );
    }

    if (currentStep === 'success') {
        const isProviderFlow = selectedDirection === 'provider';
        return (
            <IsaTransferSuccessStep
                onClose={onClose}
                transferType={isProviderFlow ? 'provider' : 'investec'}
                providerName={isProviderFlow && providerDetails ? providerDetails.provider.name : undefined}
            />
        );
    }

    const handleContinue = () => {
        if (analytics) {
            const directionLabel = selectedDirection === 'investec' ? 'To_Investec_Save' : 'From_Another_Provider';
            analytics.trackAnalyticsClickEvent(
                'continue',
                '',
                'isa transfer options',
                'ISA Transfer',
                '',
                {},
                'click',
                `ISA_Transfer_Options|Button_Click|${directionLabel}`,
                'ISA Transfer Options'
            );
        }

        if (selectedDirection === 'investec') {
            setCurrentStep('to-investec');
        } else if (selectedDirection === 'provider') {
            setCurrentStep('to-provider');
        }
    };

    return (
        <main className="isa-transfer-options">
            <h1>Transfer your ISA</h1>

            {hasActiveExternalTransfer && (
                <IdsSectionNotice
                    appearance="information"
                    className="isa-transfer-options__transfer-in-progress-notice"
                    title="You have a cash ISA transfer in progress"
                >
                    You can only have one external cash ISA transfer in progress at a time. You can start another external transfer once your current transfer is complete, but you can still transfer between your Investec Save cash ISAs.
                </IdsSectionNotice>
            )}

            <div className="isa-transfer-options__intro">
                <h2>Which ISA transfer would you like to make?</h2>
                <p>Your money will remain in a tax-free ISA whichever option you choose.</p>
            </div>

            <div className="isa-transfer-options__choices">
                <label
                    className={`isa-transfer-options__choice${selectedDirection === 'investec' ? ' isa-transfer-options__choice--selected' : ''}`}
                >
                    <input
                        type="radio"
                        name="isa-transfer-direction"
                        value="investec"
                        checked={selectedDirection === 'investec'}
                        onChange={() => {
                            setSelectedDirection('investec');
                            if (analytics) {
                                analytics.trackAnalyticsClickEvent(
                                    'select to investec',
                                    '',
                                    'isa transfer options',
                                    'ISA Transfer',
                                    '',
                                    {},
                                    'click',
                                    'ISA_Transfer_Options|Radio_Select|To_Investec_Save',
                                    'ISA Transfer Options'
                                );
                            }
                        }}
                    />
                    <span className="isa-transfer-options__choice-radio">
                        {selectedDirection === 'investec' && <span className="isa-transfer-options__choice-radio-dot" />}
                    </span>
                    <span className="isa-transfer-options__choice-content">
                            <strong>To another Investec Save cash ISA</strong>
                            <span>Transfer some or all of your savings from this account to another Investec Save cash ISA.</span>
                        </span>
                </label>

                <label
                    className={`isa-transfer-options__choice${selectedDirection === 'provider' ? ' isa-transfer-options__choice--selected' : ''}${isProviderTransferDisabled ? ' isa-transfer-options__choice--disabled' : ''}`}
                >
                    <input
                        type="radio"
                        name="isa-transfer-direction"
                        value="provider"
                        checked={selectedDirection === 'provider'}
                        disabled={isProviderTransferDisabled}
                        onChange={() => {
                            if (isProviderTransferDisabled) return;
                            setSelectedDirection('provider');
                            if (analytics) {
                                analytics.trackAnalyticsClickEvent(
                                    'select from provider',
                                    '',
                                    'isa transfer options',
                                    'ISA Transfer',
                                    '',
                                    {},
                                    'click',
                                    'ISA_Transfer_Options|Radio_Select|From_Another_Provider',
                                    'ISA Transfer Options'
                                );
                            }
                        }}
                    />
                    <span className="isa-transfer-options__choice-radio">
                        {selectedDirection === 'provider' && <span className="isa-transfer-options__choice-radio-dot" />}
                    </span>
                    <span className="isa-transfer-options__choice-content">
                            <strong>From another provider to Investec Save</strong>
                            <span>Transfer some or all of your cash ISA savings with another provider into this Instant Access Cash ISA.</span>
                        </span>
                </label>
            </div>

            {isIsaTransfersDisabled && !hasActiveExternalTransfer && (
                <IdsSectionNotice
                    appearance="warning"
                    className="isa-transfer-options__transfers-disabled-notice"
                    title="Transfers are temporarily unavailable"
                >
                    We&apos;re sorry for any inconvenience caused. If you would like to transfer your ISA to
                    Investec Save, please check back for when transfers are available again.
                </IdsSectionNotice>
            )}

            <p className="isa-transfer-options__guidance-copy">
                Get&nbsp;
                <IdsLink
                    className="isa-transfer-options__guidance-link"
                    href={`#transfer-out-guidance-${accountId}`}
                    size="md"
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                        event.preventDefault();
                        if (analytics) {
                            analytics.trackAnalyticsClickEvent(
                                'get more information',
                                '',
                                'isa transfer options',
                                'ISA Transfer',
                                '',
                                {},
                                'click',
                                'ISA_Transfer_Options|Link_Click|Get_More_Information',
                                'ISA Transfer Options'
                            );
                        }
                        setShowTransferOutGuidance(true);
                    }}
                >
                    more information
                </IdsLink>
                {' '}about transferring this cash ISA to another provider.
            </p>

            {showEmptyState && (
                <div className="isa-transfer-options__empty-state">
                    <div className="isa-transfer-options__empty-state-copy">
                        <h3>You don&apos;t currently have another eligible Investec Save cash ISA</h3>
                        <p>
                            Consider opening a Fixed Rate Cash ISA and continue earning tax-free
                            interest. Once your new account is open, you&apos;ll need to return
                            here to make the transfer.
                        </p>
                    </div>
                    <div className="isa-transfer-options__empty-state-card">
                        <div className="isa-transfer-options__empty-state-card-info">
                            <h4>1-Year Fixed<br />Rate Cash ISA</h4>
                            <div className="isa-transfer-options__empty-state-card-details">
                                <IdsDisplayTag label="Annual interest" color="brand" appearance="subtle" />
                                <div className="isa-transfer-options__empty-state-card-rate">
                                    <span className="isa-transfer-options__empty-state-card-rate-value">5.00%</span>
                                    <span className="isa-transfer-options__empty-state-card-rate-label">AER/gross*</span>
                                </div>
                            </div>
                            <IdsButton appearance="primary" size="sm" onClick={() => {
                                if (analytics) {
                                    analytics.trackAnalyticsClickEvent(
                                        'open new cash isa',
                                        '/new-account/fisa',
                                        'isa transfer options',
                                        'ISA Transfer',
                                        '',
                                        {},
                                        'click',
                                        'ISA_Transfer_Options|Button_Click|Open_New_Cash_ISA',
                                        'ISA Transfer Options'
                                    );
                                }
                                navigate('/new-account/fisa');
                            }}>
                                Open a new cash ISA
                            </IdsButton>
                        </div>
                    </div>
                </div>
            )}

            <div className="isa-transfer-options__actions">
                <IdsButton appearance="default" size="md" onClick={() => {
                    if (analytics) {
                        analytics.trackAnalyticsClickEvent(
                            'back',
                            '',
                            'isa transfer options',
                            'ISA Transfer',
                            '',
                            {},
                            'click',
                            'ISA_Transfer_Options|Button_Click|Back',
                            'ISA Transfer Options'
                        );
                    }
                    onBack();
                }}>
                    Back
                </IdsButton>
                {showContinueAction && (
                    <IdsButton appearance="primary" size="md" onClick={handleContinue}>
                        Continue
                    </IdsButton>
                )}
            </div>

            <IsaTransferInstructionsModal
                isOpen={showTransferOutGuidance}
                onClose={() => setShowTransferOutGuidance(false)}
                onReturnToDashboard={onClose}
                isDisaTransfer
            />
        </main>
    );
};

export default IsaTransferOptionsStep;
